import api from "./api"
import type { ApprovalUser, ApproverCandidate } from "./checklists"

/**
 * İçerik tipi detay sayfasının 3. sütunu: notlar, ekler, paylaşım, onay.
 *
 * Kullanıcı özeti tipleri (`ApprovalUser`, `ApproverCandidate`) ve
 * `approvalUserLabel` kontrol listesinden PAYLAŞILIYOR: iki tarafta da aynı
 * `select` ile geliyorlar, ikinci bir kopya iki yerde birden bakım demek olurdu.
 */
export type { ApprovalUser, ApproverCandidate }
export { approvalUserLabel } from "./checklists"

/**
 * `CANCELLED` yalnız ANTET revizyon zincirinde üretiliyor (yeni revizyon
 * yayınlanınca öncekiler düşer); içerik tipleri tek kayıt üzerinde ilerlediği
 * için orada görülmüyor. Tip ortak: iki belge türü aynı sekmeleri paylaşıyor.
 */
export type ContentTypeApprovalStatus =
  | "DRAFT"
  | "IN_REVIEW"
  | "PUBLISHED"
  | "CANCELLED"
export type ContentTypeApprovalDecision = "PENDING" | "APPROVED" | "REJECTED"
export type ContentTypeEventType =
  | "CREATED" | "SUBMITTED" | "REMINDED" | "APPROVED" | "REJECTED"
  | "PUBLISHED" | "CANCELLED" | "APPROVER_REMOVED" | "REVOKED"
  | "PREPARER_CHANGED"

export interface ContentTypeNote {
  id: string
  contentTypeId: string
  /** Zengin metin (HTML). */
  body: string
  sortOrder: number
  createdByUserId: string | null
  createdAt: string
  updatedAt: string
}

export interface ContentTypeAttachment {
  id: string
  contentTypeId: string
  fileId: string
  title: string | null
  sortOrder: number
  createdAt: string
  file: { id: string; filename: string; mimeType: string; size: number }
}

export interface ContentTypeShare {
  id: string
  companyId: string
  createdAt: string
  company: { id: string; name: string }
}

export interface ContentTypeApproval {
  id: string
  userId: string
  decision: ContentTypeApprovalDecision
  note: string | null
  decidedAt: string | null
  notifiedAt: string | null
  sortOrder: number
  user: ApprovalUser
}

export interface ContentTypeEvent {
  id: string
  type: ContentTypeEventType
  note: string | null
  createdAt: string
  user: ApprovalUser | null
}

export interface ContentTypeApprovalState {
  id: string
  key: string
  label: string
  code: string | null
  revisionNo: string | null
  approvalStatus: ContentTypeApprovalStatus
  activeVersionId: string | null
  effectiveDate: string | null
  publishedAt: string | null
  publishedByUserId: string | null
  createdByUserId: string | null
  /** HAZIRLAYAN — belgeyi hazır sayan tek kişi; onaya yalnız o gönderebilir. */
  preparedByUserId: string | null
  preparedByName: string | null
  approvals: ContentTypeApproval[]
  events: ContentTypeEvent[]
}

/**
 * COLLAB KAYNAĞI — notlar/ekler/paylaşım/onay sekmelerinin bağlandığı belge.
 *
 * İki belge türü var: FORM (`/admin/content-types/:key`) ve ANTET
 * (`/company-templates/:id`). Uçların adları ve gövde şekilleri birebir aynı;
 * değişen yalnız kök adres. Sekme bileşenleri bu yüzden anahtar/kimlik yerine
 * bu nesneyi alıyor — aynı ekranın iki kopyasını bakmak yerine tek kopya.
 *
 * `anahtar` React Query önbellek önekidir; iki belge türünün önbelleği
 * karışmasın diye kaynağın kendisiyle birlikte taşınıyor.
 */
export interface CollabKaynak {
  kok: string
  anahtar: readonly string[]
  /**
   * Faaliyet dosyası notları: bağlantı yokken de yazılır (çevrimdışı komut
   * kuyruğu). Form/antet notları tasarım ekranı — çevrimiçi kalır.
   */
  cevrimdisiProjeId?: string
}

/** Form (içerik tipi) kaynağı — sayfa anahtarla açılıyor. */
export const formKaynagi = (key: string): CollabKaynak => ({
  kok: `/admin/content-types/${encodeURIComponent(key)}`,
  anahtar: ["content-types", key],
})

/** Antet kaynağı — antet sayfası kimlikle açılıyor. */
export const antetKaynagi = (id: string): CollabKaynak => ({
  kok: `/company-templates/${id}`,
  anahtar: ["company-templates", id],
})

const kok = (k: CollabKaynak) => k.kok

// === Notlar ===

export async function getContentTypeNotes(k: CollabKaynak) {
  const { data } = await api.get<ContentTypeNote[]>(`${kok(k)}/notes`)
  return data
}

export async function createContentTypeNote(k: CollabKaynak, body: string) {
  const { data } = await api.post<ContentTypeNote>(`${kok(k)}/notes`, { body })
  return data
}

export async function updateContentTypeNote(k: CollabKaynak, noteId: string, body: string) {
  const { data } = await api.patch<ContentTypeNote>(`${kok(k)}/notes/${noteId}`, { body })
  return data
}

export async function deleteContentTypeNote(k: CollabKaynak, noteId: string) {
  await api.delete(`${kok(k)}/notes/${noteId}`)
}

export async function reorderContentTypeNotes(k: CollabKaynak, ids: string[]) {
  const { data } = await api.patch<ContentTypeNote[]>(`${kok(k)}/notes/reorder`, { ids })
  return data
}

// === Ekler ===

export async function getContentTypeAttachments(k: CollabKaynak) {
  const { data } = await api.get<ContentTypeAttachment[]>(`${kok(k)}/attachments`)
  return data
}

export async function addContentTypeAttachment(k: CollabKaynak, fileId: string, title?: string) {
  const { data } = await api.post<ContentTypeAttachment>(`${kok(k)}/attachments`, { fileId, title })
  return data
}

export async function deleteContentTypeAttachment(k: CollabKaynak, attachmentId: string) {
  await api.delete(`${kok(k)}/attachments/${attachmentId}`)
}

// === Paylaşım ===

export async function getContentTypeShares(k: CollabKaynak) {
  const { data } = await api.get<ContentTypeShare[]>(`${kok(k)}/shares`)
  return data
}

export async function setContentTypeShares(k: CollabKaynak, companyIds: string[]) {
  const { data } = await api.put<ContentTypeShare[]>(`${kok(k)}/shares`, { companyIds })
  return data
}

// === Onay ===

export async function getContentTypeApprovalState(k: CollabKaynak) {
  const { data } = await api.get<ContentTypeApprovalState>(`${kok(k)}/approval`)
  return data
}

export async function getContentTypeApproverCandidates(k: CollabKaynak) {
  const { data } = await api.get<ApproverCandidate[]>(`${kok(k)}/approver-candidates`)
  return data
}

/**
 * REVİZYON ZİNCİRİ satırı. Sürümden ayrı bir eksen: sürüm bir yayının
 * dondurulmuş şeması, revizyon kendi onay sürecinden geçen ayrı bir kayıt.
 */
export interface CollabRevision {
  id: string
  key: string
  label: string
  code: string | null
  revisionNo: string | null
  revisionSeq: number
  revisionDate: string | null
  effectiveDate: string | null
  isCurrentRevision: boolean
  approvalStatus: ContentTypeApprovalStatus
  publishedAt: string | null
  revisionStartedAt: string | null
  revisionNote: string | null
}

export async function getCollabRevisions(k: CollabKaynak) {
  const { data } = await api.get<CollabRevision[]>(`${kok(k)}/revisions`)
  return data
}

/** Yeni revizyon başlatır; dönen `id` yeni TASLAĞIN kimliği. */
export async function createCollabRevision(
  k: CollabKaynak,
  payload: { revisionNo?: string; revisionDate?: string; note?: string } = {},
) {
  const { data } = await api.post<{ id: string; revisions: CollabRevision[] }>(
    `${kok(k)}/revisions`,
    payload,
  )
  return data
}

export async function deleteCollabRevision(k: CollabKaynak, revisionId: string) {
  const { data } = await api.delete<CollabRevision[]>(`${kok(k)}/revisions/${revisionId}`)
  return data
}

/** HAZIRLAYAN devri — onay sürecinde reddedilir (409). */
export async function setContentTypePreparer(k: CollabKaynak, userId: string) {
  const { data } = await api.put<{ preparedByUserId: string; preparedByName: string | null }>(
    `${kok(k)}/preparer`,
    { userId },
  )
  return data
}

export async function setContentTypeApprovers(k: CollabKaynak, userIds: string[]) {
  const { data } = await api.put<ContentTypeApproval[]>(`${kok(k)}/approvers`, { userIds })
  return data
}

/** Tek onaycıyı çıkarır — onay SÜRECİNDE de çalışır (liste baştan yazılamaz). */
export async function removeContentTypeApprover(k: CollabKaynak, userId: string) {
  const { data } = await api.delete<ContentTypeApproval[]>(`${kok(k)}/approvers/${userId}`)
  return data
}

export async function submitContentTypeForApproval(k: CollabKaynak, note?: string) {
  const { data } = await api.post<ContentTypeApprovalState>(`${kok(k)}/approval/submit`, { note })
  return data
}

export async function withdrawContentTypeApproval(k: CollabKaynak, note?: string) {
  const { data } = await api.post<ContentTypeApprovalState>(`${kok(k)}/approval/withdraw`, { note })
  return data
}

export async function remindContentTypeApprovers(k: CollabKaynak) {
  const { data } = await api.post<ContentTypeApprovalState>(`${kok(k)}/approval/remind`, {})
  return data
}

export async function decideContentTypeApproval(
  k: CollabKaynak,
  decision: "APPROVED" | "REJECTED",
  note?: string,
) {
  const { data } = await api.post<ContentTypeApprovalState>(`${kok(k)}/approval/decision`, {
    decision,
    note,
  })
  return data
}

export async function publishContentTypeApproved(k: CollabKaynak, note?: string) {
  const { data } = await api.post<{ version: unknown; state: ContentTypeApprovalState }>(
    `${kok(k)}/approval/publish`,
    { note },
  )
  return data
}

/** Yayındaki belgeyi yeniden düzenlemeye açar (taslağa döner). */
export async function reopenContentType(k: CollabKaynak, note?: string) {
  const { data } = await api.post<ContentTypeApprovalState>(`${kok(k)}/approval/reopen`, { note })
  return data
}

/** Onay sekmesindeki rozet metni — tek yerden okunsun. */
export const APPROVAL_STATUS_LABEL: Record<ContentTypeApprovalStatus, string> = {
  CANCELLED: "İptal",
  DRAFT: "Taslak",
  IN_REVIEW: "Onayda",
  PUBLISHED: "Yayında",
}

export const EVENT_LABEL: Record<ContentTypeEventType, string> = {
  CREATED: "Oluşturuldu",
  SUBMITTED: "Onaya gönderildi",
  REMINDED: "Hatırlatma gönderildi",
  APPROVED: "Onaylandı",
  REJECTED: "Reddedildi",
  PUBLISHED: "Yayınlandı",
  CANCELLED: "Geri çekildi",
  APPROVER_REMOVED: "Onaycı çıkarıldı",
  REVOKED: "Düzenlemeye açıldı",
  PREPARER_CHANGED: "Hazırlayan devredildi",
}
